import React from 'react';
import { makeStyles, Button, TextField, Typography, Box, FormControl, Select, MenuItem, Grid } from '@material-ui/core';
import {
  barcodeImageSrcGenerator,
  janBarcodeImageSrcGenerator,
  qrBarcodeImageSrcGenerator,
  StaticBarcode,
} from '@sbgift-coupon/use';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';

// ==================== CommonButton ====================
type CommonButtonProps = {
  label: string;
  variant?: 'primary' | 'secondary' | 'danger';
  fullWidth?: boolean;
  onClick?: () => void;
  disabled?: boolean;
  type?: 'button' | 'submit' | 'reset';
};

const useButtonStyles = makeStyles((theme) => ({
  root: {
    borderRadius: 999,
    height: 56,
    fontSize: 16,
    fontWeight: 600,
    textTransform: 'none',
    padding: '15px 0px !important',
  },
  primary: {
    color: '#FF3B30',
    border: '1px solid #FF3B30',
    backgroundColor: '#ffffff',
    '&:hover': {
      backgroundColor: '#ffffff',
    },
  },
  danger: {
    backgroundColor: '#FF3B30',
    color: '#ffffff',
    border: 'none',
    '&:hover': {
      backgroundColor: '#E53935',
    },
  },
  secondary: {
    backgroundColor: '#D0D0D0',
    color: '#ffffff',
    border: 'none',
    '&:hover': {
      backgroundColor: '#D0D0D0',
    },
  },
  disabled: {
    opacity: 0.7,
  },
}));

export const CommonButton = ({
  label,
  variant = 'primary',
  fullWidth = true,
  onClick,
  disabled = false,
  type,
}: CommonButtonProps): JSX.Element => {
  const classes = useButtonStyles();

  return (
    <Button
      fullWidth={fullWidth}
      disabled={disabled}
      onClick={onClick}
      type={type}
      className={`${classes.root} ${classes[variant]} ${disabled ? classes.disabled : ''}`}
    >
      {label}
    </Button>
  );
};

// ==================== CommonInput ====================
type CommonInputProps = {
  label: string;
  required?: boolean;
  maxLength?: number;
  value?: string | number;
  onChange?: (value: string) => void;
  onPaste?: (e: React.ClipboardEvent<HTMLInputElement>) => void;
  placeholder?: string;
  error?: boolean;
  helperText?: string;
  type?: React.HTMLInputTypeAttribute;
  fullWidth?: boolean;
  disabled?: boolean;
};

const useInputStyles = makeStyles(() => ({
  inputLabel: {
    fontSize: 14,
    color: '#7C7C7C',
    fontWeight: 600,
  },
  inputLabelRequired: {
    fontSize: 12,
    color: '#F94F4F',
    marginLeft: '4px',
    fontWeight: 300,
  },
  outlinedInput: {
    borderRadius: '10px',
    backgroundColor: '#ffffff',
    border: '1px solid #DCDCDC',
    '&:hover': {
      backgroundColor: '#ffffff',
    },
    '&.Mui-focused': {
      backgroundColor: '#ffffff',
    },
    '&.Mui-disabled': {
      backgroundColor: '#ffffff',
    },
    '&.Mui-error': {
      border: '1px solid #f94f4f',
    },
  },
  underlineBefore: {
    '&:before': {
      borderBottom: 'none !important',
    },
    '&:after': {
      borderBottom: 'none !important',
    },
  },
  placeholder: {
    '&::placeholder': {
      color: '#BDBDBD',
      opacity: 1,
    },
  },
}));

export const CommonInput = ({
  label,
  required,
  maxLength,
  value,
  onChange,
  onPaste,
  placeholder,
  error,
  helperText,
  type = 'text',
  fullWidth = true,
  disabled,
}: CommonInputProps): JSX.Element => {
  const classes = useInputStyles();

  /** keep only number */
  const normalizeNumber = (value: string): string => {
    if (!value) return '';
    return value.replace(/[^\d]/g, '');
  };

  return (
    <Box display="flex" flexDirection="column" maxWidth={'500px'}>
      {/* Label */}
      <Box display="flex" alignItems="center" mb={0.5}>
        <Typography className={classes.inputLabel}>{label}</Typography>

        {required && <Typography className={classes.inputLabelRequired}>*必須</Typography>}
      </Box>
      {/* Input */}
      <TextField
        fullWidth={fullWidth}
        value={value}
        onChange={(e) => {
          const raw = normalizeNumber(e.target.value); // remove commas
          if (onChange) onChange(raw); // update RHF with pure number string
        }}
        onPaste={onPaste}
        placeholder={placeholder}
        error={error}
        helperText={helperText}
        type={type}
        disabled={disabled}
        InputProps={{
          classes: {
            root: classes.outlinedInput,
            underline: classes.underlineBefore,
            input: classes.placeholder,
          },
        }}
        inputProps={{
          maxLength,
          inputMode: type === 'number' || type === 'tel' ? 'numeric' : undefined,
          pattern: type === 'number' || type === 'tel' ? '[0-9]*' : undefined,
          style: {
            fontSize: 16,
            padding: '15px 20px',
            fontWeight: 400,
            color: '#000000',
            height: '26px',
          },
        }}
      />
    </Box>
  );
};

// ==================== CommonSelect ====================
type SelectOption = {
  value: string;
  label: string;
  amount?: number;
};

type CommonSelectProps = {
  label: string;
  required?: boolean;
  value?: string | number;
  onChange: (value: string) => void;
  error?: boolean;
  helperText?: string;
  options: SelectOption[];
  disabled?: boolean;
};

const useSelectStyles = makeStyles((theme) => ({
  formControl: {
    width: '166px',
    borderBottom: 'none',
  },
  inputLabel: {
    fontSize: 14,
    color: '#7C7C7C',
    fontWeight: 600,
  },
  inputLabelRequired: {
    fontSize: 12,
    color: '#F94F4F',
    marginLeft: theme.spacing(0.5),
    fontWeight: 300,
  },
  select: {
    borderRadius: '12px',
    backgroundColor: theme.palette.common.white,
    color: '#292929',
    fontSize: 16,
    fontWeight: 600,
    boxShadow: '0px 2px 8px 0px #0000001A',
    width: '100%',
    maxWidth: 166,
    '& .MuiSelect-select': {
      padding: '16px 40px 16px 24px',
      backgroundColor: 'transparent',
      '&:focus': {
        backgroundColor: 'transparent',
        borderRadius: 12,
      },
    },
    '& .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
    '&.Mui-disabled': {
      cursor: 'not-allowed',
      opacity: 0.6,
    },
  },
  selectError: {
    border: '1px solid #F94F4F',
  },
  icon: {
    color: '#EE4079',
    right: 12,
  },
  helperText: {
    margin: '3px 0 0 14px',
    fontSize: 12,
    color: '#F94F4F',
  },
  menuPaper: {
    maxHeight: 300,
    borderRadius: 12,
  },
  menuItem: {
    fontSize: 16,
    fontWeight: 500,
    padding: '8px 24px',
  },
  itemAmount: {
    color: '#FF3B30',
    position: 'absolute',
    right: '20px',
    fontWeight: 500,
    fontSize: 16,
    '& span': {
      color: '#303030',
      fontSize: 12,
    },
  },
}));

export const CommonSelect = ({
  label,
  required,
  value,
  onChange,
  options,
  error,
  helperText,
  disabled,
}: CommonSelectProps): JSX.Element => {
  const classes = useSelectStyles();

  const handleChange = (e: React.ChangeEvent<{ value: unknown }>): void => {
    onChange(e.target.value as string);
  };

  return (
    <FormControl className={classes.formControl}>
      {/* Label */}
      <Box display="flex" alignItems="center" mb={0.5}>
        <Typography className={classes.inputLabel}>{label}</Typography>
        {required && <Typography className={classes.inputLabelRequired}>*必須</Typography>}
      </Box>

      {/* Select */}
      <Select
        className={`${classes.select}${error ? ` ${classes.selectError}` : ''}`}
        value={value || ''}
        onChange={handleChange}
        disabled={disabled}
        variant="outlined"
        IconComponent={KeyboardArrowDownIcon}
        MenuProps={{
          PaperProps: {
            className: classes.menuPaper,
          },
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'left',
          },
          transformOrigin: {
            vertical: 'top',
            horizontal: 'left',
          },
          getContentAnchorEl: null,
        }}
        classes={{
          icon: classes.icon,
        }}
        renderValue={(selected) => {
          const selectedOption = options.find((opt) => opt.value === selected);
          return selectedOption?.label || '';
        }}
      >
        {options.map((opt) => (
          <MenuItem key={opt.value} value={opt.value} className={classes.menuItem}>
            {opt.label}
            {!!opt.amount && (
              <p className={classes.itemAmount}>
                {opt.amount?.toLocaleString('en-US')}
                <span>円分</span>
              </p>
            )}
          </MenuItem>
        ))}
      </Select>

      {/* Helper text */}
      {error && helperText && <Typography className={classes.helperText}>{helperText}</Typography>}
    </FormControl>
  );
};

// ==================== StaticBarcodes ====================
type StaticBarcodesProps = {
  staticBarcodes?: StaticBarcode[];
  imageServer: string;
};

const useStaticBarcodesStyles = makeStyles((theme) => ({
  section: {
    marginTop: theme.spacing(1),
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
  },
  barcodeImage: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    maxWidth: '100%',
  },
}));

export const StaticBarcodes: React.FC<StaticBarcodesProps> = (props) => {
  const classes = useStaticBarcodesStyles();

  const checkSrcBarcodeType = (barcode: StaticBarcode): string => {
    if (barcode.barcodeType === 'qr') {
      return qrBarcodeImageSrcGenerator({
        barcode: {
          barcodeType: barcode.barcodeType,
          barcodeValue: barcode.barcode,
          imageServer: props.imageServer,
        },
        size: '10',
      });
    } else if (barcode.barcodeType === 'code128' || barcode.barcodeType === 'nw7') {
      return barcodeImageSrcGenerator({
        barcode: {
          barcodeType: barcode.barcodeType,
          barcodeValue: barcode.barcode,
          imageServer: props.imageServer,
        },
      });
    } else if (barcode.barcodeType === 'jan13' || barcode.barcodeType === 'jan8') {
      return janBarcodeImageSrcGenerator({
        barcode: {
          barcodeType: barcode.barcodeType,
          barcodeValue: barcode.barcode,
          imageServer: props.imageServer,
        },
      });
    } else return '';
  };

  return (
    <>
      {props.staticBarcodes?.map((barcode) => {
        return (
          <>
            <Grid item xs={12} key={barcode.barcode} className={classes.section}>
              {barcode.barcode && barcode.barcodeType !== 'none' ? (
                <Box textAlign={'center'}>
                  <img className={classes.barcodeImage} alt={barcode.barcode} src={checkSrcBarcodeType(barcode)} />
                </Box>
              ) : (
                <></>
              )}
            </Grid>
          </>
        );
      })}
    </>
  );
};
